/**
 * The camera mode's geometry — Lite's `RenderModeView.viewfinder`: Auto is the whole
 * stage; a fixed aspect is a frame across the full width between black bars, fitted by
 * height when it would not fit.
 */
export type CameraAspect = 'auto' | '4:3' | '16:9' | '1:1' | '3:4';
export const CAMERA_ASPECTS: Array<{ id: CameraAspect; label: string; ratio: number | null }> = [
  { id: 'auto', label: 'Auto', ratio: null },
  { id: '4:3', label: '4:3', ratio: 4 / 3 },
  { id: '16:9', label: '16:9', ratio: 16 / 9 },
  { id: '1:1', label: '1:1', ratio: 1 },
  { id: '3:4', label: '3:4', ratio: 3 / 4 },
];
export const CAMERA_ZOOMS: Array<{ label: string; value: number }> = [
  { label: '0,5', value: 0.5 }, { label: '1×', value: 1 }, { label: '2', value: 2 }, { label: '3', value: 3 },
];

export interface Rect { x: number; y: number; width: number; height: number }

/** Where the frame sits on a stage of `size`, leaving room for the top bar and the shutter row. */
export function viewfinderRect(size: { width: number; height: number }, aspect: CameraAspect, insets = { top: 56, bottom: 168 }): Rect {
  const ratio = CAMERA_ASPECTS.find((a) => a.id === aspect)?.ratio ?? null;
  if (!ratio) return { x: 0, y: 0, width: size.width, height: size.height };
  const available = Math.max(1, size.height - insets.top - insets.bottom);
  let width = size.width, height = width / ratio;
  if (height > available) { height = available; width = height * ratio; }
  return { x: (size.width - width) / 2, y: insets.top + (available - height) / 2, width, height };
}

/** A CSS-pixel rect on the stage → the same rect in canvas pixels. */
export function cropForCanvas(rect: Rect, canvas: { width: number; height: number; clientWidth: number; clientHeight: number }): Rect {
  const sx = canvas.width / Math.max(1, canvas.clientWidth), sy = canvas.height / Math.max(1, canvas.clientHeight);
  return { x: Math.round(rect.x * sx), y: Math.round(rect.y * sy), width: Math.max(1, Math.round(rect.width * sx)), height: Math.max(1, Math.round(rect.height * sy)) };
}

/** Unity's photo animation: a white flash held briefly, then faded out. */
export const FLASH_HOLD_MS = 100;
export const FLASH_FADE_MS = 600;
